import React from 'react';
import {
  Button,
  Card, Divider, Typography, useTheme,
} from '@mui/material';
import type { PreApproveType } from '../../types/preloan/v3/index';
import { Partner } from '../LeadFormIndex/types';
import AlertDialog from '../AlertDialog';
import BancameAlert from '../Alert';
import fetcher from '../../utils/fetcher';
import getProductTypeLabels from './helpers';

type PreApproveCardProps = {
  preApprove: PreApproveType;
  partner: Partner;
  token: string;
  isSelected?: boolean;
  onSelect?: () => void;
};

function monthsToYears(months: number) {
  return (months / 12).toLocaleString('es-cl');
}

/**
 * Resumen de consentimiento para transferencia entre responsables.
 * Categorías, plazos y derechos: ver política de privacidad enlazada arriba.
 */
function TransferDataConsentText(props: { destinationName: string }) {
  const { destinationName } = props;
  return (
    <div style={{
      marginTop: '10px', fontSize: 11, fontStyle: 'italic', textAlign: 'justify',
    }}
    >
      <br />
      <span>
        Al postular, autorizo la
        {' '}
        <strong>comunicación o transferencia de mis datos personales</strong>
        {' '}
        desde quien administra este canal hacia
        {' '}
        <strong>{destinationName}</strong>
        {', solo para evaluar y dar seguimiento a mi postulación. Declaro un consentimiento libre e '}
        informado y revocable cuando corresponda, según la
        {' '}
        <strong>Ley N.º 21.719</strong>
        {' '}
        y la política de privacidad aplicable.
      </span>
    </div>
  );
}

export default function PreApproveCard(props: PreApproveCardProps) {
  const {
    preApprove, partner, token, isSelected = false, onSelect,
  } = props;
  const theme = useTheme();
  const bancameGreen = theme.palette.tertiary.main;
  const valueColor = isSelected
    ? bancameGreen
    : (partner.config.styles?.secondaryColor ?? theme.palette.secondary.main);
  const labelColor = isSelected ? '#ffffff' : 'gray';
  const [openTransferAlert, setOpenTransferAlert] = React.useState(false);
  const [transferError, setTransferError] = React.useState(false);
  const [transferErrorMessage, setTransferErrorMessage] = React.useState('Ocurrió un error al enviar tu postulación.');
  const [transferSuccess, setTransferSuccess] = React.useState(false);
  const solicitarEnabled = false;
  const handleTransferPreApprove = async (preApproveId: string) => {
    try {
      setOpenTransferAlert(false);
      setTransferError(false);
      await fetcher(
        `${process.env.REACT_APP_API_URL}/pre-loan-request/partner-entity-transfer`,
        'POST',
        { preApproveId },
        token,
      );
      setTransferError(false);
      setOpenTransferAlert(true);
      setTransferSuccess(true);
    } catch (e: any) {
      setTransferError(true);
      setTransferErrorMessage(typeof e === 'string' ? e : 'Ocurrió un error al enviar tu postulación.');
      setOpenTransferAlert(true);
      setTransferSuccess(false);
    }
  };

  const preApproveSummary = (PA: PreApproveType) => (
    <div>
      <div>
        <Typography variant="body2" fontWeight="bold" gutterBottom style={{ color: isSelected ? '#ffffff' : undefined }}>
          {getProductTypeLabels(PA.productType, PA.productSubtype)}
        </Typography>
        <Divider style={{ borderColor: isSelected ? 'rgba(255,255,255,0.2)' : undefined }} />
        <Typography variant="caption" style={{ color: labelColor }}>
          Monto deuda máxima
        </Typography>
        <Typography
          variant="h6"
          fontWeight="bold"
          style={{ color: valueColor, marginTop: '5px' }}
        >
          {PA && `UF ${PA.maxAmount.toLocaleString('es-CL')}`}
        </Typography>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-evenly', marginBlock: '5px' }}>
        <div>
          <Typography variant="caption" style={{ color: labelColor }}>
            Plazo máximo
          </Typography>
          <Typography variant="body1" style={{ color: valueColor }}>
            {PA && monthsToYears(PA.maxPeriods)}
            {' '}
            <span>años</span>
          </Typography>
        </div>
        <div>
          <Typography variant="caption" style={{ color: labelColor }}>
            Interés anual UF
          </Typography>
          <Typography variant="body1" style={{ color: valueColor }}>
            {PA && PA.rate.toLocaleString('es-cl', {
              style: 'percent',
              minimumFractionDigits: 3,
            })}
          </Typography>
        </div>
      </div>
      {/* TODO: remove solicitarEnabled after testing */}
      {PA.isTransferrable && solicitarEnabled && (
        <AlertDialog
          action={() => handleTransferPreApprove(PA.id)}
          button={(
            <Button
              disabled={PA.alreadyTransferred || transferSuccess}
              variant={isSelected ? 'outlined' : 'contained'}
              color="secondary"
              style={isSelected ? { borderColor: '#ffffff', color: '#ffffff' } : undefined}
            >
              {PA.alreadyTransferred ? 'POSTULACIÓN ENVIADA' : 'SOLICITAR'}
            </Button>
          )}
          submitButtonLabel="SOLICITAR"
          dialogTitle={(
            <Typography align="center" variant="h5" color="secondary">
              ¡Felicitaciones!
            </Typography>
          )}
          dialogContent={(
            <div style={{ marginBlock: '10px' }}>
              <Typography variant="body1">
                Eres elegible para un
                {' '}
                <b>{getProductTypeLabels(PA.productType, PA.productSubtype)}</b>
                ,
                que será gestionado por
                {' '}
                <b>{PA.toTransferPartnerName}</b>
                .
              </Typography>
              <TransferDataConsentText destinationName={PA.toTransferPartnerName} />
            </div>
          )}
        />
      )}
      {((PA.isTransferrable && PA.alreadyTransferred) || transferSuccess) && (
        <Typography fontSize={10} variant="caption" fontStyle="italic">
          <Typography
            fontSize={10}
            variant="caption"
            style={{ color: isSelected ? '#ffffff' : undefined }}
            color={isSelected ? undefined : 'secondary'}
          >
            ¡Tu postulación ya ha sido enviada!
          </Typography>
          <div style={{ color: isSelected ? '#ffffff' : undefined }}>
            Se te contactará de
            {' '}
            <b>{PA.toTransferPartnerName}</b>
            {' '}
            para continuar el proceso.
          </div>
        </Typography>
      )}
      <BancameAlert
        openAlert={openTransferAlert}
        successMessage="Postulación enviada correctamente."
        errorMessage={transferErrorMessage}
        error={transferError}
      />
    </div>
  );

  return (
    <Card
      elevation={0}
      onClick={onSelect}
      style={{
        margin: '1em auto auto',
        padding: '5px',
        width: '240px',
        textAlign: 'center',
        borderRadius: '10px',
        cursor: onSelect ? 'pointer' : 'default',
        backgroundColor: isSelected ? theme.palette.primary.main : undefined,
        boxShadow: 'none',
        transform: isSelected ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.3s ease-out',
        border: isSelected
          ? `2px solid ${theme.palette.primary.main}`
          : '2px solid #E0E0E0',
        boxSizing: 'border-box',
      }}
    >
      {preApproveSummary(preApprove)}
    </Card>
  );
}

PreApproveCard.defaultProps = {
  isSelected: false,
  onSelect: undefined,
};
